import {
  ArrayMaxSize,
  ArrayMinSize,
  IsArray,
  IsIn,
  IsInt,
  IsOptional,
  IsString,
  Max,
  MaxLength,
  Min,
  MinLength,
} from 'class-validator';

/** The one free-form field an anonymous visitor sends — clamped hard, injected as fenced data.
 *  300, not a one-liner's 120: the field asks what their CONTENT is about, and a pasted caption is a fair answer. */
export const QUIZ_GEN_INPUT_MAX_LENGTH = 300;
/** Followers' comments a brief may carry: past this the block is a wall, and the thread quotes one anyway. */
export const QUIZ_GEN_COMMENTS_MAX = 15;
export const QUIZ_GEN_COMMENT_MAX_LENGTH = 200;
/** Reel transcripts a brief may carry, and how much of each: the reel is the lesson, cut where a lesson's point is made. */
export const QUIZ_GEN_REELS_MAX = 10;
export const QUIZ_GEN_TRANSCRIPT_MAX_LENGTH = 1500;
export const QUIZ_GEN_INPUT_MIN_LENGTH = 8;

/** Every generated quiz has exactly this many questions — a prompt hard rule the validator enforces. */
export const QUIZ_GEN_QUESTION_COUNT = 10;

/** An answer must sit on one line of the post kit's tile at 1080 wide; the kit's test proves the tile holds this many. */
export const QUIZ_OPTION_MAX_CHARS = 26;

/**
 * The languages a quiz's FRAME (hook, questions, options) can be written in —
 * ISO 639-1. The material under test keeps the subject's own language: a
 * Russian-framed quiz about English slang still quotes the slang in English.
 * An allowlist, not free text, because the code is stored on the quiz.
 */
/**
 * The one line the post kit says in its own voice, written by the generator in the
 * quiz's frame language. Stored on the quiz (`quiz.settings.kit`); a quiz without
 * one gets the stock English line. Kits stored before 2026-10-01 also carry
 * `ask`, `sticker` and `tap`, which nothing reads any more.
 */
export interface QuizKitCopy {
  /** The last slide: where the full quiz is, the link in her bio. Kits from 2026-10-01 to 2026-10-09 asked for a comment word in a `{word}` slot instead. */
  door: string;
}
/** How many of the quiz's questions the post kit's carousel shows, without their answers. */
export const KIT_QUESTION_COUNT = 4;

/** The slot the comment-word doors carried; a stored door holding it asks for a word nothing answers. */
export const RETIRED_WORD_SLOT = '{word}';

export const QUIZ_KIT_MAX_CHARS: Record<keyof QuizKitCopy, number> = {
  door: 70,
};

export const QUIZ_LANGUAGES = ['en', 'ru', 'uk', 'es', 'fr', 'de', 'tr', 'pt', 'it', 'pl'] as const;
export type QuizLanguage = (typeof QUIZ_LANGUAGES)[number];

/**
 * One question of a written draft, swapped for a fresh one on the same brief.
 * The other questions travel as their texts only — enough for the writer to
 * avoid a fact already tested and an opener already used.
 */
export class SwapQuizQuestionBody {
  @IsString()
  @MinLength(QUIZ_GEN_INPUT_MIN_LENGTH)
  @MaxLength(QUIZ_GEN_INPUT_MAX_LENGTH)
  what!: string;

  @IsOptional()
  @IsString()
  @MaxLength(QUIZ_GEN_INPUT_MAX_LENGTH)
  audience?: string;

  /** Pins the frame language; absent (a quiz stored without one), the writer matches the listed questions. */
  @IsOptional()
  @IsIn(QUIZ_LANGUAGES)
  language?: QuizLanguage;

  /** The language the subject teaches, as the quiz recorded it (`QuizSource.taught`). */
  @IsOptional()
  @IsIn(QUIZ_LANGUAGES)
  taught?: QuizLanguage;

  /** The creator's voice, as the quiz recorded it (`QuizSource.voice`). */
  @IsOptional()
  @IsString()
  @MaxLength(QUIZ_GEN_INPUT_MAX_LENGTH)
  voice?: string;

  @IsString()
  @MaxLength(200)
  title!: string;

  @IsArray()
  @ArrayMinSize(QUIZ_GEN_QUESTION_COUNT)
  @ArrayMaxSize(QUIZ_GEN_QUESTION_COUNT)
  @IsString({ each: true })
  @MaxLength(500, { each: true })
  questions!: string[];

  /** 0-based position of the question being replaced. */
  @IsInt()
  @Min(0)
  @Max(QUIZ_GEN_QUESTION_COUNT - 1)
  index!: number;
}

export interface GeneratedQuizOptionDto {
  label: string;
  isCorrect: boolean;
}

export interface GeneratedQuizQuestionDto {
  text: string;
  /**
   * What a beginner in this niche wrongly believes here — generated BEFORE the
   * options so the wrong option is derived from a real misconception, and kept
   * (not discarded) so a result screen can later echo the player's specific miss.
   */
  misconception: string;
  options: GeneratedQuizOptionDto[];
}

/**
 * What a quiz was written from, carried by the quiz itself so a Swap is written on the same
 * input as the rest; absent on a quiz written before 2026-09-30, which a Swap then guesses.
 */
export interface QuizSource {
  /** The line the generator was given ("what"), as it sanitized it. */
  brief?: string;
  /** Who the students are, in the provider's words. */
  audience?: string;
  /** The language the subject teaches, when a reader of the creator named it. */
  taught?: QuizLanguage;
  /** How the creator talks, in one line: a Swap is written in the same voice. */
  voice?: string;
}

export interface GeneratedQuizDto extends QuizSource {
  title: string;
  questions: GeneratedQuizQuestionDto[];
  /** The language the frame was written in — pinned by the request or read off the audience. */
  language: QuizLanguage;
  /** The post kit's lines (`QuizKitCopy`). Absent on a draft an older prompt wrote. */
  kit?: QuizKitCopy;
  /** Written with what is going viral with the audience in hand; absent when it was not. */
  trended?: boolean;
  /** How many of this week's waiting comments the writer read first; absent when none were. */
  askedBy?: number;
  /**
   * Which prompt wrote this quiz (QUIZ_GEN_PROMPT_VERSION at generation time).
   * Stamped so quality regressions are attributable when the ruleset changes.
   */
  promptVersion: number;
  /** The writer's log row for this draft (`quiz_generation`); the quiz made from it names it back. */
  generationId?: string;
}
